import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { FolderTreeNode } from "../services/fileApi";
import {
	buildUploadPlan,
	ChangedChoice,
	classifyIncoming,
	countDealFiles,
	flattenDealFiles,
	mergeIncoming,
	needsHash,
	sha256Hex,
	summarizeReview,
} from "../utils/addFilesReview";
import { relativePathFor } from "../utils/folderUpload";

/**
 * State for reviewing files before they are added to an existing deal
 * (DOP-608): the files under review, each one's classification against the
 * room, and the analyst's choice for every file that already exists there.
 *
 * Only files whose path the room already holds are hashed — a new file needs
 * no comparison, so a large first-time drop never reads its bytes twice.
 */
export const useDealFileReview = (deal: FolderTreeNode, initialFiles: File[]) => {
	const existing = useMemo(() => flattenDealFiles(deal), [deal]);
	const [files, setFiles] = useState<File[]>(initialFiles);
	const [hashes, setHashes] = useState<Map<File, string | null>>(
		() => new Map()
	);
	const [choices, setChoices] = useState<Record<string, ChangedChoice>>({});
	// Files already sent for hashing, so a re-render never starts a second read.
	const requested = useRef(new WeakSet<File>());

	useEffect(() => {
		const pending = files.filter(
			(file) => needsHash(file, existing) && !requested.current.has(file)
		);
		for (const file of pending) {
			requested.current.add(file);
			void sha256Hex(file).then((hash) => {
				setHashes((current) => new Map(current).set(file, hash));
			});
		}
	}, [files, existing]);

	const items = useMemo(
		() => classifyIncoming(files, existing, hashes),
		[files, existing, hashes]
	);

	const addFiles = useCallback((added: File[]) => {
		setFiles((current) => mergeIncoming(current, added));
	}, []);

	const removeFile = useCallback((path: string) => {
		setFiles((current) => current.filter((file) => relativePathFor(file) !== path));
	}, []);

	const setChoice = useCallback((path: string, choice: ChangedChoice) => {
		setChoices((current) => ({ ...current, [path]: choice }));
	}, []);

	const buildPlan = useCallback(
		() => buildUploadPlan(items, choices, existing),
		[items, choices, existing]
	);

	return {
		files,
		items,
		choices,
		existingCount: countDealFiles(deal),
		summary: summarizeReview(items, choices),
		isChecking: items.some((item) => item.status === "checking"),
		addFiles,
		removeFile,
		setChoice,
		buildPlan,
	};
};
